import { functionsOfPosition } from '../constants/account-functions.constant';
import { AccountBindingSource, IProjectAccount } from '../models/project-account.model';

/**
 * REV-22479 — the Kontenplan: every account of the closing with its position and its function.
 *
 * One cell per half, and each half is in exactly one of five states — the same states a document's
 * account cell has in the Dokumente hub:
 *
 *   working   the AI is classifying this half right now
 *   proposal  a value nobody has decided on yet (AI, KMU range table, last year's closing)
 *   decided   a value a person accepted or picked
 *   open      no value — the user picks one
 *   none      the half does not apply (a function where the position carries none, or no position yet)
 *
 * WHO set a proposal is deliberately not shown: AI, range table and inheritance look the same.
 */
export type ChartCellState = 'working' | 'proposal' | 'decided' | 'open' | 'none';

export type ChartHalf = 'position' | 'function';

export interface ChartOption {
    readonly key: string;
    readonly label: string;
}

export interface ChartCell {
    readonly state: ChartCellState;
    /** The stored value — `null` in `working`, `open` and `none`. */
    readonly value: string | null;
    /** The value's label, for the chip. `null` without a value. */
    readonly label: string | null;
}

export interface ChartRow {
    readonly accountId: string;
    readonly accountNumber: string;
    readonly name: string;
    readonly position: ChartCell;
    readonly function: ChartCell;
    /** What the function picker offers — what the rule table allows under the row's position. */
    readonly functionOptions: readonly ChartOption[];
}

export interface ChartLabels {
    position(key: string): string;
    accountFunction(key: string): string;
}

/** A value a person set is decided; anything else that carries a value is still a proposal. */
const DECIDED_SOURCE: AccountBindingSource = 'MANUAL';

function cellOf(
    value: string | null,
    source: AccountBindingSource | null,
    working: boolean,
    labelOf: (key: string) => string,
): ChartCell {
    if (working) {
        return { state: 'working', value: null, label: null };
    }
    if (value === null) {
        return { state: 'open', value: null, label: null };
    }

    return { state: source === DECIDED_SOURCE ? 'decided' : 'proposal', value, label: labelOf(value) };
}

const NONE: ChartCell = { state: 'none', value: null, label: null };

/** Account numbers in the order a chart of accounts is read: numerically where they are numbers. */
function byAccountNumber(a: IProjectAccount, b: IProjectAccount): number {
    return a.accountNumber.localeCompare(b.accountNumber, undefined, { numeric: true });
}

export function presentChartOfAccounts(accounts: readonly IProjectAccount[], labels: ChartLabels): ChartRow[] {
    return [...accounts].sort(byAccountNumber).map((account) => {
        const position = cellOf(
            account.accountGroup,
            account.groupSource,
            account.groupClassification === 'PENDING',
            (key) => labels.position(key),
        );
        // A function only exists under a position that has one. While the position is still being
        // classified there is nothing to say about the function either.
        const allowed = position.state === 'working' ? [] : functionsOfPosition(account.accountGroup);
        // A stored function the position does not allow (an old value like POSTAL, retired on
        // 15.10.2026) is no proposal: the backend would refuse it. The user picks a valid one.
        const storedFunction =
            account.accountFunction !== null && allowed.includes(account.accountFunction)
                ? account.accountFunction
                : null;
        const accountFunction =
            allowed.length === 0
                ? NONE
                : cellOf(storedFunction, account.functionSource, account.functionClassification === 'PENDING', (key) =>
                      labels.accountFunction(key),
                  );

        return {
            accountId: account.accountId,
            accountNumber: account.accountNumber,
            name: account.name,
            position,
            function: accountFunction,
            functionOptions: allowed.map((key) => ({ key, label: labels.accountFunction(key) })),
        };
    });
}

/**
 * The accounts without a position. Over the list it is given: the default view leaves out the accounts
 * that are zero in every period shown, and those need no position to carry on (REV-22504).
 */
export function accountsWithoutPosition(accounts: readonly IProjectAccount[]): IProjectAccount[] {
    return accounts.filter((account) => account.accountGroup === null);
}

/**
 * Whether the Kontenplan is complete: every account the user works with has a position — proposed or
 * decided. The function is not required. Drives the gate in front of the reconciliation and the
 * overview.
 *
 * Over the DEFAULT list (`stepAccounts`): accounts without a balance in any period shown may stay
 * without a position. `null` while that list is unknown. No account at all is complete.
 */
export function isChartOfAccountsCompleteFor(accounts: readonly IProjectAccount[] | null): boolean | null {
    if (accounts === null) {
        return null;
    }

    return accountsWithoutPosition(accounts).length === 0;
}

/** The counters above the table: what is still with the AI, and what the user still has to look at. */
export function chartOfAccountsCounts(rows: readonly ChartRow[]): {
    readonly working: number;
    readonly proposals: number;
    readonly open: number;
} {
    const halves = rows.flatMap((row) => [row.position, row.function]);

    return {
        working: halves.filter((cell) => cell.state === 'working').length,
        proposals: halves.filter((cell) => cell.state === 'proposal').length,
        open: rows.filter((row) => row.position.state === 'open').length,
    };
}
